import { useState } from "react";
import { Button } from "@altimateai/lego";
import { LoadingButton } from "@altimateai/lego";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@altimateai/lego";
import {
  TestCase,
  CreateTestCaseRequest,
  Criterion,
} from "../../services/evals";
import { useTeamOptions } from "../hooks/useTeamOptions";
import { useInternalContext } from "../../context/InternalContext";
import { useAppState } from "../../hooks/useAppState";
import { TestCaseForm } from "./TestCaseForm";
import { TestCaseFormData } from "./testCaseFormTypes";

interface CreateTestCaseModalProps {
  testCase?: TestCase | null;
  onClose: () => void;
  onSubmit: (data: CreateTestCaseRequest) => void;
  criteria?: Criterion[];
  loading?: boolean;
}

export const CreateTestCaseModal: React.FC<CreateTestCaseModalProps> = ({
  testCase,
  onClose,
  onSubmit,
  criteria,
  loading = false,
}) => {
  const isEditing = !!testCase;

  // Convert criteria object to array for easier editing
  const criteriaToArray = (criteria: Record<string, string> = {}) => {
    return Object.entries(criteria).map(([name, description]) => ({
      name,
      description,
    }));
  };

  const { currentTenant } = useInternalContext();
  const { rbacEnabled } = useAppState();
  const { teamOptions, teamsLoading } = useTeamOptions();

  const [formData, setFormData] = useState<TestCaseFormData>({
    name: testCase?.name || "",
    description: testCase?.description || "",
    turns: testCase?.turns?.map((t) => ({
      user_message: t.user_message || "",
      expected_ai_response: t.expected_ai_response || "",
    })) || [{ user_message: "", expected_ai_response: "" }],
    criteria: criteriaToArray(testCase?.criteria) || [],
    tags: testCase?.tags || [],
    tenant: testCase?.tenant || "All",
    team_id: testCase?.team_id || null,
    custom_agents:
      testCase?.custom_agents?.map((a) => ({
        name: a.name || "",
        description: a.description || "",
        prompt: a.prompt || "",
      })) || [],
    user_view: testCase?.user_view
      ? {
          ...testCase.user_view,
          entity_type: testCase.user_view.entity_type || "",
          workflowId: testCase.user_view.workflowId || "",
          runId: testCase.user_view.runId,
        }
      : null,
  });

  const handleSubmit = () => {
    // Convert criteria array back to object
    const criteriaObj = formData.criteria.reduce(
      (acc, c) => {
        if (c.name.trim() && c.description.trim()) {
          acc[c.name.trim()] = c.description.trim();
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    onSubmit({
      ...formData,
      criteria: criteriaObj,
    });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="al-max-w-screen-xl al-max-h-[90vh] al-overflow-y-auto">
        <div>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Edit Test Case" : "Create Test Case"}
            </DialogTitle>
          </DialogHeader>
        </div>

        <TestCaseForm
          formData={formData}
          onChange={setFormData}
          currentTenant={currentTenant}
          rbacEnabled={rbacEnabled ?? false}
          teamOptions={teamOptions}
          teamsLoading={teamsLoading}
          availableCriteria={criteria}
          allowTurnManagement={true}
        />

        <div>
          <DialogFooter>
            <Button
              size="sm"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <LoadingButton loading={loading} onClick={handleSubmit}>
              {isEditing ? "Update Test Case" : "Create Test Case"}
            </LoadingButton>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
